import { useState, useEffect } from "react";
import length from "@turf/length";
import { lineString } from "@turf/helpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Map, Pencil, Trash2, Navigation, Droplets, MapPin, Hexagon, PenTool, Route, Square, Maximize2, Minimize2, GitCommit } from "lucide-react";
import { calculateAcres } from "../utils/mapCalculations";
import MapStyleSwitcher from "../../../components/MapStyleSwitcher";

const DRAW_MODE_OPTIONS = [
  { id: "polygon", label: "Polygon", icon: Hexagon, hint: "Click vertices · double-click to finish" },
  { id: "freehand", label: "Freehand", icon: PenTool, hint: "Press & drag to outline" },
  { id: "trail", label: "Trail", icon: Route, hint: "Draw a line, auto-buffered to width" },
  { id: "parcel", label: "Parcel", icon: Square, hint: "Click any parcel on the map to use it as the area" },
];

const PropertyMapSection = ({
  mapContainerRef,
  mapRef,
  propertyAddress,
  polygons,
  totalAcres,
  isDrawing,
  onStartDrawing,
  onCancelDrawing,
  onRemovePolygon,
  onRebufferTrail,
  onRenamePolygon,
  drawMode,
  onDrawModeChange,
  trailWidthFeet,
  onTrailWidthChange,
  followParcelLine,
  onFollowParcelLineChange,
  boundaryChoice,
  wrongPropertyMode,
  onWrongPropertyModeChange,
  onSkipMap,
  onGoToCoordinates,
  showWetlands,
  onToggleWetlands,
  onReloadCustomLayers,
}) => {
  const [coordsText, setCoordsText] = useState("");
  const [coordError, setCoordError] = useState("");
  const [skipMapChecked, setSkipMapChecked] = useState(false);
  const [useCoordinatesChecked, setUseCoordinatesChecked] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Exit fullscreen on ESC + tell Mapbox to recalc canvas dimensions whenever toggled
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    // Body scroll lock while fullscreen
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullscreen]);

  useEffect(() => {
    // Mapbox needs to know the container size changed
    const map = mapRef?.current;
    if (!map) return;
    // Two resizes — once immediately, once after the CSS transition settles
    map.resize();
    const t = setTimeout(() => map.resize(), 250);
    return () => clearTimeout(t);
  }, [isFullscreen, mapRef]);

  const handleWrongPropertyChange = (checked) => {
    if (onWrongPropertyModeChange) {
      onWrongPropertyModeChange(checked);
    }
    if (!checked) {
      setSkipMapChecked(false);
      setUseCoordinatesChecked(false);
    }
  };

  const handleSkipMapChange = (checked) => {
    setSkipMapChecked(checked);
    if (checked) {
      setUseCoordinatesChecked(false);
      if (onSkipMap) onSkipMap(true);
    } else {
      if (onSkipMap) onSkipMap(false);
    }
  };

  const handleUseCoordinatesChange = (checked) => {
    setUseCoordinatesChecked(checked);
    if (checked) {
      setSkipMapChecked(false);
      if (onSkipMap) onSkipMap(false);
    }
  };

  const handleGoToCoordinates = () => {
    // Accept: "35.99094462475151, -78.7696039540684" (Google/iPhone format),
    // also tolerant of space, tab, or pipe separators, parens, and ° symbols.
    const cleaned = (coordsText || "").trim().replace(/[()°NSEW]/gi, "");
    const parts = cleaned.split(/[\s,|]+/).filter(Boolean);
    if (parts.length !== 2) {
      setCoordError("Paste in the format: 35.990945, -78.769604");
      return;
    }
    const lat = parseFloat(parts[0]);
    const lon = parseFloat(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      setCoordError("Latitude must be between -90 and 90, longitude between -180 and 180.");
      return;
    }
    setCoordError("");
    if (onGoToCoordinates) onGoToCoordinates(lat, lon);
  };

  const showMap = !skipMapChecked && (propertyAddress || polygons.length > 0);

  return (
    <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
      <CardHeader className="border-b border-zinc-100 px-6 py-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg uppercase tracking-tight text-[#00341d] flex items-center gap-2">
            <Map className="w-5 h-5" />
            Property Area
          </CardTitle>
          <div className="flex items-center gap-4">
            {totalAcres > 0 && (
              <div className="bg-[#00341d] text-white px-4 py-1.5 rounded-full text-sm font-bold">
                {totalAcres.toFixed(2)} acres total
              </div>
            )}
            {polygons.length > 0 && (
              <span className="text-sm text-zinc-500">
                {polygons.length} area{polygons.length > 1 ? "s" : ""} selected
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {/* Wrong Property Checkbox */}
        {propertyAddress && (
          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="wrong-property"
                checked={wrongPropertyMode}
                onCheckedChange={handleWrongPropertyChange}
                data-testid="wrong-property-checkbox"
              />
              <Label htmlFor="wrong-property" className="text-sm text-amber-800 cursor-pointer">
                Wrong property returned from search?
              </Label>
            </div>

            {wrongPropertyMode && (
              <div className="mt-3 pl-6 space-y-3">
                {/* Option 1: Skip map */}
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="skip-map"
                    checked={skipMapChecked}
                    onCheckedChange={handleSkipMapChange}
                    data-testid="skip-map-checkbox"
                  />
                  <div>
                    <Label htmlFor="skip-map" className="text-sm font-medium cursor-pointer">
                      Remove map, use input address only
                    </Label>
                    <p className="text-xs text-zinc-500">The address you entered will be used without parcel data</p>
                  </div>
                </div>

                {/* Option 2: Use coordinates */}
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="use-coordinates"
                    checked={useCoordinatesChecked}
                    onCheckedChange={handleUseCoordinatesChange}
                    data-testid="use-coordinates-checkbox"
                  />
                  <div className="flex-1">
                    <Label htmlFor="use-coordinates" className="text-sm font-medium cursor-pointer">
                      Enter coordinates to view property lines
                    </Label>
                    <p className="text-xs text-zinc-500 mb-2">Enter lat/lon to see parcel boundaries, then draw your area</p>
                    
                    {useCoordinatesChecked && (
                      <div className="mt-2 space-y-1">
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-xs text-zinc-500">GPS coordinates (paste from Google Maps or iPhone)</Label>
                            <Input
                              type="text"
                              placeholder="35.99094462475151, -78.7696039540684"
                              value={coordsText}
                              onChange={(e) => { setCoordsText(e.target.value); if (coordError) setCoordError(""); }}
                              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleGoToCoordinates())}
                              className="h-8 text-sm font-mono"
                              data-testid="coord-input"
                            />
                          </div>
                          <Button
                            onClick={handleGoToCoordinates}
                            size="sm"
                            className="bg-[#00341d] hover:bg-[#002816] h-8"
                            data-testid="go-to-coords-button"
                          >
                            <Navigation className="w-4 h-4 mr-1" />
                            Go
                          </Button>
                        </div>
                        {coordError ? (
                          <p className="text-xs text-red-600" data-testid="coord-error">{coordError}</p>
                        ) : (
                          <p className="text-xs text-zinc-400">Format: <span className="font-mono">latitude, longitude</span> (comma-separated decimals)</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {showMap ? (
          <div
            className={
              isFullscreen
                ? "fixed inset-0 z-[60] bg-white"
                : "relative"
            }
            data-testid="property-map-wrapper"
          >
            <div
              ref={mapContainerRef}
              className={isFullscreen ? "h-full w-full" : "h-[400px] w-full"}
              data-testid="property-map"
            />

            {/* Top-right: Fullscreen toggle (always visible, top of stack) */}
            <button
              type="button"
              onClick={() => setIsFullscreen((v) => !v)}
              className="absolute top-4 right-4 z-20 bg-white/95 backdrop-blur border border-zinc-200 rounded-md shadow-lg w-9 h-9 flex items-center justify-center text-zinc-700 hover:bg-zinc-50 hover:text-[#00341d] transition-colors"
              title={isFullscreen ? "Exit fullscreen (ESC)" : "Fullscreen map"}
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              data-testid="map-fullscreen-toggle"
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>

            {/* Top-left: Mode selector */}
            <div
              className="absolute top-4 left-4 z-10 bg-white/95 backdrop-blur border border-zinc-200 rounded-md shadow-lg p-2 max-w-[320px]"
              data-testid="draw-mode-selector"
            >
              <div className="flex items-center gap-1">
                {DRAW_MODE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const selected = drawMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={isDrawing}
                      onClick={() => onDrawModeChange?.(opt.id)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded transition-colors ${
                        selected
                          ? "bg-[#00341d] text-white"
                          : "bg-white text-zinc-700 hover:bg-zinc-100"
                      } ${isDrawing ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid={`draw-mode-${opt.id}`}
                      title={opt.hint}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {drawMode === "trail" && (
                <div className="mt-2 pt-2 border-t border-zinc-200 flex items-center gap-2" data-testid="trail-width-row">
                  <Label className="text-xs text-zinc-500 whitespace-nowrap">Trail width (ft)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={1000}
                    step={1}
                    value={trailWidthFeet || ""}
                    onChange={(e) => onTrailWidthChange?.(parseFloat(e.target.value) || 0)}
                    disabled={isDrawing}
                    className="h-8 w-20"
                    data-testid="trail-width-input"
                  />
                  <span className="text-[10px] text-zinc-400">Rounded turns</span>
                </div>
              )}

              {/* Follow-property-line toggle — only visible while actively drawing a polygon */}
              {drawMode === "polygon" && isDrawing && (
                <div className="mt-2 pt-2 border-t border-zinc-200" data-testid="follow-parcel-row">
                  <button
                    type="button"
                    onClick={() => onFollowParcelLineChange?.(!followParcelLine)}
                    className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium rounded transition-colors ${
                      followParcelLine
                        ? "bg-emerald-600 text-white"
                        : "bg-white text-zinc-700 hover:bg-zinc-100 border border-zinc-200"
                    }`}
                    data-testid="follow-property-line-toggle"
                  >
                    <GitCommit className="w-3.5 h-3.5" />
                    {followParcelLine ? "Following property line — click next vertex" : "Follow property line for next segment"}
                  </button>
                  {followParcelLine && (
                    <p className="text-[10px] text-emerald-700 mt-1 leading-snug">
                      Your next click will snap the segment to the parcel boundary. You&apos;ll pick shorter or longer route.
                    </p>
                  )}
                </div>
              )}
              <p className="text-[10px] text-zinc-400 mt-1.5 leading-snug">
                {DRAW_MODE_OPTIONS.find((m) => m.id === drawMode)?.hint || ""}
              </p>
            </div>

            <div className="absolute bottom-4 left-4 flex gap-2 z-10">
              {!isDrawing ? (
                <Button
                  onClick={() => onStartDrawing?.(drawMode)}
                  className="bg-[#d97706] hover:bg-[#b45309] text-white shadow-lg"
                  data-testid="draw-area-button"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Draw Area
                </Button>
              ) : (
                <Button
                  onClick={onCancelDrawing}
                  variant="outline"
                  className="bg-white shadow-lg"
                  data-testid="cancel-drawing-button"
                >
                  Cancel Drawing
                </Button>
              )}
              <Button
                onClick={onToggleWetlands}
                variant="outline"
                className={`shadow-lg ${showWetlands ? "bg-cyan-600 text-white hover:bg-cyan-700 border-cyan-600" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
                data-testid="toggle-wetlands-button"
              >
                <Droplets className="w-4 h-4 mr-2" />
                Wetlands
              </Button>
            </div>
            <div className="absolute bottom-4 right-4 z-10">
              <MapStyleSwitcher mapRef={mapRef} onStyleLoad={onReloadCustomLayers} />
            </div>

            {/* Boundary-path chooser overlay — appears after the user places a
                Follow-Property-Line vertex; two ring routes render on the map
                (green = shorter, amber = longer) and the user picks one. */}
            {boundaryChoice && (
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/98 backdrop-blur border border-zinc-300 rounded-lg shadow-xl p-4 z-20 max-w-sm w-[92%]"
                data-testid="boundary-choice-popup"
              >
                <h4 className="text-sm font-semibold text-zinc-800 mb-1">Pick the route</h4>
                <p className="text-xs text-zinc-500 mb-3">
                  Two paths trace the parcel boundary between your last two clicks. Choose the one that fits the clearing area.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={boundaryChoice.pickShorter}
                    className="flex flex-col items-start gap-0.5 rounded-md px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                    data-testid="boundary-choose-shorter"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider">Shorter</span>
                    <span className="text-[11px] text-emerald-100">
                      {Math.round(boundaryChoice.shorter_length_ft).toLocaleString()} ft
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={boundaryChoice.pickLonger}
                    className="flex flex-col items-start gap-0.5 rounded-md px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                    data-testid="boundary-choose-longer"
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider">Longer</span>
                    <span className="text-[11px] text-amber-100">
                      {Math.round(boundaryChoice.longer_length_ft).toLocaleString()} ft
                    </span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={boundaryChoice.cancel}
                  className="mt-2 w-full text-[11px] text-zinc-500 hover:text-zinc-700 py-1"
                  data-testid="boundary-choose-cancel"
                >
                  Cancel — go back to straight lines
                </button>
              </div>
            )}

            {polygons.length > 0 && (
              <div className="absolute top-4 right-16 bg-white/95 backdrop-blur rounded-lg shadow-lg p-3 max-w-xs z-10" data-testid="designated-areas-panel">
                <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-2">Designated Areas</p>
                {polygons.map((poly, index) => (
                  <PolygonRow
                    key={`polygon-${index}`}
                    poly={poly}
                    index={index}
                    onRemove={() => onRemovePolygon(index)}
                    onRebuffer={onRebufferTrail ? (w) => onRebufferTrail(index, w) : null}
                    onRename={onRenamePolygon ? (name) => onRenamePolygon(index, name) : null}
                  />
                ))}
                <div className="border-t border-zinc-200 mt-2 pt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-[#00341d]">{totalAcres.toFixed(2)} ac</span>
                </div>
              </div>
            )}
          </div>
        ) : skipMapChecked ? (
          <div className="h-[120px] flex items-center justify-center text-zinc-500 bg-zinc-100">
            <div className="text-center">
              <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="font-medium">Map disabled</p>
              <p className="text-sm">Using address: {propertyAddress}</p>
            </div>
          </div>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-zinc-400 bg-zinc-50">
            <div className="text-center">
              <Map className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Enter a property address to view map</p>
              <p className="text-sm">You can then draw areas to clear</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PropertyMapSection;

// ---------- Polygon row (renders trail width inline editor for trail polygons) ----------
const PolygonRow = ({ poly, index, onRemove, onRebuffer, onRename }) => {
  const isTrail = poly?._meta?.kind === "trail";
  const [editing, setEditing] = useState(false);
  const [draftWidth, setDraftWidth] = useState(poly?._meta?.width_ft || 20);

  // Trail centerline length (computed from the saved _meta.line)
  let trailFeet = 0;
  let trailMiles = 0;
  if (isTrail && Array.isArray(poly._meta.line) && poly._meta.line.length >= 2) {
    try {
      trailMiles = length(lineString(poly._meta.line), { units: "miles" });
      trailFeet = trailMiles * 5280;
    } catch { /* swallow malformed coords */ }
  }

  const commit = () => {
    const w = parseFloat(draftWidth);
    if (Number.isFinite(w) && w > 0 && w !== poly._meta.width_ft) {
      onRebuffer?.(w);
    }
    setEditing(false);
  };

  return (
    <div
      className="flex items-center justify-between py-1 text-sm group gap-2"
      data-testid={`polygon-row-${index}`}
    >
      <div className="flex flex-col min-w-0">
        {onRename ? (
          <input
            value={poly._name || ""}
            onChange={(e) => onRename(e.target.value)}
            placeholder={`Area ${index + 1}`}
            className="text-zinc-700 bg-transparent border-b border-transparent hover:border-zinc-200 focus:border-[#00341d] focus:outline-none text-sm w-full"
            data-testid={`polygon-name-${index}`}
          />
        ) : (
          <span className="text-zinc-700">Area {index + 1}</span>
        )}
        {isTrail && (
          <span className="mt-0.5 text-[10px] uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-700 font-medium self-start">
            Trail
          </span>
        )}
        {isTrail && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[11px] text-zinc-500 hover:text-[#00341d] text-left"
            data-testid={`trail-width-display-${index}`}
            title="Click to change trail width"
          >
            {poly._meta.width_ft} ft wide
            {trailFeet > 0 && (
              <span className="text-zinc-400" data-testid={`trail-mileage-${index}`}>
                {" · "}{trailFeet < 1000 ? `${trailFeet.toFixed(0)} ft` : `${trailMiles.toFixed(2)} mi`}
              </span>
            )}
            <span className="text-zinc-400"> · click to edit</span>
          </button>
        )}
        {isTrail && editing && (
          <div className="flex items-center gap-1 mt-0.5">
            <Input
              type="number"
              min={1}
              max={1000}
              value={draftWidth}
              onChange={(e) => setDraftWidth(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") { setDraftWidth(poly._meta.width_ft); setEditing(false); }
              }}
              autoFocus
              className="h-6 w-16 text-xs"
              data-testid={`trail-width-edit-${index}`}
            />
            <span className="text-[11px] text-zinc-500">ft</span>
            <button
              type="button"
              onClick={commit}
              className="text-[11px] text-[#00341d] hover:underline"
              data-testid={`trail-width-save-${index}`}
            >
              Save
            </button>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="font-semibold text-[#00341d]">{calculateAcres(poly).toFixed(2)} ac</span>
        <button
          onClick={onRemove}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1"
          title="Delete area"
          data-testid={`polygon-remove-${index}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};
